<script lang="ts">
  import { det, monteCarloResult, tasks, allowWeekendWork, baselineSetAt, twoShifts } from "../lib/stores";
  import { fmt1 } from "../lib/format";
  import { formatCivilDate } from "../lib/projectDate";
  import { baselineSchedule } from "../lib/summary";
  import Gantt from "./Gantt.svelte";
  import MonteCarloPanel from "./MonteCarloPanel.svelte";

  $: baseline = baselineSchedule($tasks, $allowWeekendWork, $twoShifts ? 2 : 1);
  $: baselineDays = baseline ? baseline.projectEnd : null;
  $: baselineValue = baselineDays != null ? fmt1(baselineDays) : "—";
  $: baselineNote = $baselineSetAt
    ? `Captured ${formatCivilDate($baselineSetAt)}`
    : "Not captured yet — using each activity's estimate from when it was added";

  $: deterministicValue = $det.ok ? fmt1($det.projectEnd) : "—";
  $: deterministicNote = $det.ok
    ? (() => {
        if (baselineDays == null) return "no baseline available";
        const diff = $det.projectEnd - baselineDays;
        return (diff === 0 ? "matches" : diff > 0 ? "+" + fmt1(diff) + "d vs" : fmt1(diff) + "d vs") + " the baseline above";
      })()
    : "longest path through the network above";

  $: p50Value = $monteCarloResult ? fmt1($monteCarloResult.p50) : "—";
  $: rangeValue = $monteCarloResult ? fmt1($monteCarloResult.p10) + "–" + fmt1($monteCarloResult.p90) : "—";
  $: mergeBiasNote =
    $monteCarloResult && $det.ok
      ? $monteCarloResult.p50 - $det.projectEnd > 0.05
        ? `+${fmt1($monteCarloResult.p50 - $det.projectEnd)}d merge bias vs deterministic`
        : "≈ deterministic critical path"
      : "run simulation to populate";
</script>

<div class="tab-panel group">
  <div class="group-head">
    <span class="group-tag analysis">Analysis</span>
    <span class="group-desc">Computed live from the Lean planning activities — critical path, KPIs, and Monte Carlo bandwidth. Nothing here is editable.</span>
  </div>

  <section>
    <div class="section-head"><div class="section-title">Result</div></div>
    <div class="kpi-row">
      <div class="kpi">
        <div class="kpi-label">Baseline (captured plan)</div>
        <div class="kpi-value">{baselineValue} <span>days</span></div>
        <div class="kpi-note">{baselineNote}</div>
      </div>
      <div class="kpi">
        <div class="kpi-label">Critical path (deterministic, M)</div>
        <div class="kpi-value">{deterministicValue} <span>days</span></div>
        <div class="kpi-note">{deterministicNote}</div>
      </div>
      <div class="kpi">
        <div class="kpi-label">Monte Carlo P50</div>
        <div class="kpi-value">{p50Value} <span>days</span></div>
        <div class="kpi-note">{mergeBiasNote}</div>
      </div>
      <div class="kpi">
        <div class="kpi-label">Monte Carlo P10 – P90</div>
        <div class="kpi-value">{rangeValue} <span>days</span></div>
        <div class="kpi-note">80% confidence band</div>
      </div>
    </div>
  </section>

  <section>
    <div class="section-head"><div class="section-title">Critical path (deterministic schedule, M durations)</div></div>
    <Gantt />
  </section>

  <MonteCarloPanel />
</div>
